/**
 * SRS WebRTC 播放（基于 SRS rtc/v1/play 信令）
 * 信令优先走同源 /api/streaming/rtc/v1/play/，避免 HTTPS 页面混合内容问题。
 */
export function getStreamingHost() {
  if (typeof process !== 'undefined' && process.env.REACT_APP_STREAMING_HOST) {
    return process.env.REACT_APP_STREAMING_HOST;
  }
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return process.env.REACT_APP_STREAMING_HOST || 'zh.lqsos.cn';
    }
    return host;
  }
  return 'zh.lqsos.cn';
}

export function getWebRtcUrl(streamKey) {
  return `webrtc://${getStreamingHost()}/live/${streamKey}`;
}

export function getSrtPublishUrl(streamKey) {
  const host = getStreamingHost();
  // 短格式：与 RTMP 路径一致 live/队员名（SRS 支持 streamid=live/xxx，无需 #!::r=...,m=publish）
  return `srt://${host}:10080?streamid=live/${streamKey}`;
}

export function getRtcPlayApiUrl() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}/api/streaming/rtc/v1/play/`;
  }
  return '/api/streaming/rtc/v1/play/';
}

/**
 * 播放 WebRTC 流，返回 { pc, stream, close }
 */
export async function playSrsWebRtc(streamKey, { apiUrl, streamUrl } = {}) {
  const url = streamUrl || getWebRtcUrl(streamKey);
  const api = apiUrl || getRtcPlayApiUrl();

  const pc = new RTCPeerConnection({
    // 公网由 SRS candidate 给出；此处保留空 iceServers 即可
    iceServers: []
  });

  // 先收流
  pc.addTransceiver('audio', { direction: 'recvonly' });
  pc.addTransceiver('video', { direction: 'recvonly' });

  const stream = new MediaStream();
  pc.ontrack = (event) => {
    if (event.track) {
      stream.addTrack(event.track);
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // 等待 ICE gathering（短超时，避免卡死）
  await waitIceGatheringComplete(pc, 1500);

  const payload = {
    api,
    streamurl: url,
    clientip: null,
    sdp: pc.localDescription.sdp
  };

  const resp = await fetch(api, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined
  });

  if (!resp.ok) {
    throw new Error(`WebRTC 信令失败 HTTP ${resp.status}`);
  }

  const data = await resp.json();
  if (data.code !== 0 && data.code !== undefined) {
    throw new Error(data.msg || data.message || `WebRTC 信令错误 code=${data.code}`);
  }
  if (!data.sdp) {
    throw new Error('WebRTC 信令未返回 SDP');
  }

  await pc.setRemoteDescription(
    new RTCSessionDescription({ type: 'answer', sdp: data.sdp })
  );

  const close = () => {
    try {
      pc.getSenders().forEach((s) => {
        try { s.track && s.track.stop(); } catch (e) { /* ignore */ }
      });
      pc.getReceivers().forEach((r) => {
        try { r.track && r.track.stop(); } catch (e) { /* ignore */ }
      });
      pc.close();
    } catch (e) {
      console.warn('关闭 WebRTC 失败', e);
    }
  };

  return { pc, stream, close, url };
}

function waitIceGatheringComplete(pc, timeoutMs) {
  if (pc.iceGatheringState === 'complete') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(), timeoutMs);
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer);
        pc.removeEventListener('icegatheringstatechange', check);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', check);
  });
}

export default {
  getStreamingHost,
  getWebRtcUrl,
  getSrtPublishUrl,
  getRtcPlayApiUrl,
  playSrsWebRtc
};
